<template>
  <modal
    ref="modalComponent"
    size="xl"
    :show="show"
    :is-submitting="isSubmitting"
    submit-button-text="Checkin to all KRs"
    @hidden="$emit('input', false)"
    @submit="onSubmit"
    :no-close-on-backdrop="true"
    :no-close-on-esc="true"
    body-class="bulk-checkin"
    :scrollable="false"
  >
    <div slot="modal-title" class="heading d-flex align-items-center">
      <i class="rocket-icon mr-2"></i>
      <span class="head-title">Beast Mode</span>
    </div>
    <div class="bulk-checkin">
      <div v-if="fetchingGoals">
        <loader class="mb-5" avatar-size="24" line-height="27" />
        <loader class="mb-5" avatar-size="24" line-height="27" />
        <loader class="mb-5" avatar-size="24" line-height="27" />
        <loader class="mb-5" avatar-size="24" line-height="27" />
      </div>
      <div v-else-if="goals && goals.length > 0">
        <div class="goal" v-for="goal in goals" :key="goal.id" :id="'bulk-checkin-goal-' + goal.id">
          <div class="d-flex goal-meta">
            <i class="goal-icon flex-shrink-0"></i>
            <ownership-short
              class="goal-avatar"
              :entity="goal"
              entity-type="goal"
              length="24"
              @change="onOwnershipChange(ENTITY_TYPE.GOAL, $event)"
            ></ownership-short>
            <span class="text-big bold" v-no-capture>{{ goal.title }}</span>
          </div>
          <div class="krs text-medium">
            <div class="kr" v-for="(kr, index) in goal.keyResults" :key="kr.id">
              <div class="row mx-0 flex-nowrap">
                <div class="overdue-sign" v-if="kr.isCheckinOverdue">
                  <checkin-overdue :identifier="'bulk-check-overdue-' + kr.id" :kr="kr">
                    <template slot="field">
                      <div class="d-flex align-items-center">
                        <span class="text-error mr-1 text-small due-text">Due</span>
                        <i class="clock-icon"></i>
                      </div>
                    </template>
                  </checkin-overdue>
                </div>
                <div class="kr-circle-icon-wrapper">
                  <i class="kr-icon"></i>
                </div>
                <ownership-short
                  class="kr-avatar"
                  :entity="kr"
                  entity-type="key_result"
                  length="22"
                  @change="onOwnershipChange(ENTITY_TYPE.KEY_RESULT, $event)"
                ></ownership-short>
                <span class="col-4 pl-0 kr-title" v-no-capture>{{ kr.title }}</span>
                <div
                  class="col-auto kr-value-field text-center"
                  v-if="kr.measureBy === KR_MEASURING_UNIT.BOOLEAN"
                >
                  <b-form-checkbox
                    class="complete-kr-checkbox"
                    size="lg"
                    v-model="kr.newCurrent"
                    value="1"
                    unchecked-value="0"
                    :disabled="isLinkedToGoogleSheet(kr)"
                    @change="markAsDirty(kr.id)"
                    >Complete
                  </b-form-checkbox>
                </div>
                <div class="col-auto kr-value-field" v-else>
                  <input
                    class="form-control grey round"
                    v-autofocus="
                      sourceKrId === kr.id || (kr.goalId === sourceGoalId && index === 0)
                    "
                    :class="{
                      'errored-field':
                        errors[kr.id] &&
                        ((errors[kr.id].checkin && errors[kr.id].checkin.newCurrent) ||
                          errors[kr.id].current)
                    }"
                    :type="kr.isFetching ? 'text' : 'number'"
                    @input="onKrNumberChange(kr, $event.target.value)"
                    :value="kr.isFetching ? 'Fetching...' : kr.newCurrent"
                    :disabled="isLinkedToGoogleSheet(kr)"
                  />
                  <div class="text-small text-prime text-center mt-1">
                    <span :class="'kr-value ' + kr.measureBy">{{ kr.start }}</span>
                    <span class="pr-1 pl-1 arrow-next">➝</span>
                    <span :class="'kr-value ' + kr.measureBy">{{ kr.end }}</span>
                  </div>
                </div>
                <checkin-status-select
                  :hide-label="true"
                  class="status-select col-auto pl-0"
                  :items="statuses"
                  :value="kr.newStatus"
                  @input="onStatusChange(kr, $event)"
                  v-model="kr.newStatus"
                />
                <bulk-checkin-editor
                  ref="checkinEditor"
                  :kr-id="kr.id"
                  :errors="errors[kr.id] && errors[kr.id].comment"
                  @save="onSubmit"
                  @change="onCommentChange(kr, $event)"
                />
              </div>
              <template v-if="errors[kr.id]">
                <template v-if="errors[kr.id].current">
                  <div
                    class="field-error-message common-errors"
                    v-for="error in errors[kr.id].current"
                    :key="error"
                  >
                    {{ error }}
                  </div>
                </template>
                <template v-else-if="errors[kr.id].checkin">
                  <div
                    class="field-error-message common-errors"
                    v-for="error in flattenObjValues(errors[kr.id].checkin)"
                    :key="error"
                  >
                    {{ error }}
                  </div>
                </template>
              </template>
            </div>
          </div>
        </div>
      </div>
      <div class="no-result" v-else>
        We could not find any Goals or KR's you own
      </div>
    </div>
  </modal>
</template>

<script>
import { trackAnalyticsEvent, trackEvent } from '@/analytics'
import { CHECKIN_TYPE, EVENT_ACTION, EVENT_CATEGORY } from '@/constants/analytics'
import editManager from '@/editManager'
import GoogleSpreadsheetMixin from '@/mixins/GoogleSpreadsheetMixin'
import * as R from 'ramda'
import { BFormCheckbox } from 'bootstrap-vue'
import { MIME_TYPE } from '@/constants/common'
import { EXT_LOOKUP, KR_MEASURING_UNIT, KR_STATUS } from '@/constants/kr'
import { formatHyphenatedString } from '@/goalUtils'
import MembershipsMixin from '@/mixins/MembershipsMixin'
import Modal from '@/components/Modal'
import CheckinStatusSelect from '@/components/CheckinStatusSelect'
import BulkCheckinEditor from '@/components/BulkCheckinEditor'
import Loader from '@/components/skeletons/AvatarTitle'
import OwnershipShort from '@/components/OwnershipShort'
import CheckinOverdue from '@/components/CheckinOverdue'
import { DEFAULT_ERROR } from '@/constants/messages'
import { TOAST_TYPE } from '@/constants/toasts'
import EventBus from '@/EventBus'
import { nowInterval } from '@/interval'
import { mapActions } from 'vuex'
import { ENTITY_TYPE } from '@/constants/entities'

/**
 * This component shows a modal that helps in doing bulk checkins to user's KRs
 * Props:
 * - orgId
 *
 * @emits
 * - checkin-posted: emited when check in posted
 */
export default {
  name: 'BulkCheckIn',
  props: ['show', 'orgId', 'teamId', 'sourceGoalId', 'sourceKrId', 'source'],
  model: {
    prop: 'show'
  },
  mixins: [MembershipsMixin, GoogleSpreadsheetMixin],
  components: {
    Modal,
    CheckinStatusSelect,
    BFormCheckbox,
    Loader,
    BulkCheckinEditor,
    CheckinOverdue,
    OwnershipShort
  },
  data() {
    return {
      goals: null,
      statuses: Object.values(KR_STATUS).map(status => ({
        value: status,
        label: this.krStatusText(status),
        type: status
      })),
      dirtyKrs: new Set(),
      errors: {},
      fetchingGoals: true,
      EXT_LOOKUP,
      KR_MEASURING_UNIT,
      ENTITY_TYPE,
      isSubmitting: false
    }
  },
  computed: {
    user() {
      return this.$store.state.user
    }
  },
  methods: {
    ...mapActions(['getGoals', 'createEvent']),
    krStatusText: formatHyphenatedString,
    markAsDirty(krId) {
      this.dirtyKrs.add(krId)
    },
    onKrNumberChange(kr, newCurrent) {
      this.markAsDirty(kr.id)
      kr.newCurrent = newCurrent
    },
    onStatusChange(kr, newStatus) {
      this.markAsDirty(kr.id)
      kr.newStatus = newStatus
    },
    onCommentChange(kr, comment) {
      kr.comment = comment
      this.markAsDirty(kr.id)
    },
    hideModal() {
      this.$refs.modalComponent.hide()
    },
    krById(krId) {
      let keyResult
      this.goals.find(goal => {
        let kr = goal.keyResults.find(kr => kr.id === krId)
        keyResult = keyResult || kr
      })
      return keyResult
    },
    flattenObjValues(obj) {
      return R.flatten(Object.values(obj))
    },
    clearStoredEditorData() {
      this.$refs.checkinEditor.forEach(checkinEditor => {
        checkinEditor.showEditor && setTimeout(checkinEditor.clearEditorStoredData, 1000)
      })
    },
    onSubmit() {
      if (this.dirtyKrs.size > 0) {
        this.isSubmitting = true
        const dirtyKrsSize = this.dirtyKrs.size
        const dirtyKrs = []
        this.dirtyKrs.forEach(krId => {
          dirtyKrs.push(this.krById(krId))
        })
        const apiPromises = dirtyKrs.map(kr => {
          return this.createEvent({
            orgId: kr.organizationId,
            goalId: kr.goalId,
            event: {
              comment: kr.comment,
              commentType: MIME_TYPE.HTML,
              checkin: {
                keyResultId: kr.id,
                oldStatus: kr.status,
                newStatus: kr.newStatus,
                oldCurrent: kr.current,
                newCurrent: kr.newCurrent
              }
            }
          })
            .then(() => {
              this.dirtyKrs.delete(kr.id)
              return { success: true }
            })
            .catch(e => {
              const errorDetail = R.pathOr(
                { checkin: { error: [DEFAULT_ERROR] } },
                ['response', 'data', 'detail'],
                e
              )
              return {
                success: false,
                response: errorDetail,
                krId: kr.id
              }
            })
        })
        Promise.all(apiPromises)
          .then(results => {
            if (results.some(({ success }) => !success)) {
              // some requests have failed
              const errors = {}
              results
                .filter(x => !x.success)
                .forEach(result => (errors[result.krId] = result.response))
              this.errors = errors
              if (dirtyKrsSize > this.dirtyKrs.size) {
                EventBus.$emit('ADD_TOAST', {
                  message: 'Checkins have been posted successfully for some KRs'
                })
              }
              this.$emit('checkin-posted')
            } else {
              this.$emit('checkin-posted')
              trackAnalyticsEvent(EVENT_CATEGORY.ACTIVITY, EVENT_ACTION.BULK_CHECKIN, {
                source: this.source,
                checkinType: CHECKIN_TYPE.BULK
              })
              EventBus.$emit('ADD_TOAST', { message: 'Checkins have been posted successfully' })
              this.hideModal()
              this.clearStoredEditorData()
            }
          })
          .finally(() => (this.isSubmitting = false))
      } else {
        this.hideModal()
      }
    },
    isLinkedToGoogleSheet(kr) {
      return kr.extLookup === EXT_LOOKUP.GOOGLE_SPREADSHEET
    },
    async fetchGoals() {
      let currentInterval = nowInterval()
      try {
        this.fetchingGoals = true
        let goals = await this.getGoals({
          orgId: this.orgId,
          teamId: this.teamId,
          options: {
            intervalStart: currentInterval.start,
            intervalEnd: currentInterval.end,
            intervalSubset: currentInterval.subset,
            ownerId: this.user.id,
            isArchived: false
          }
        })
        this.goals = goals
          .map(goal => {
            const krs = goal.keyResults
              .filter(x => !x.isArchived)
              .map(kr => {
                return {
                  ...kr,
                  newCurrent: kr.current,
                  newStatus: kr.status,
                  isFetching: this.isLinkedToGoogleSheet(kr) // for showing loading information while current value is being fetched from Google Sheet
                }
              })
            return { ...goal, keyResults: krs }
          })
          .filter(goal => goal.keyResults.length > 0)
      } catch (e) {
        const message = R.pathOr(DEFAULT_ERROR, ['response', 'data', 'message'], e)
        EventBus.$emit('ADD_TOAST', { message, type: TOAST_TYPE.ERROR })
      }
      this.fetchingGoals = false
    },
    fetchSpreadSheetLinkedValues() {
      this.goals.forEach(goal => {
        goal.keyResults
          .filter(x => !x.isArchived)
          .forEach(async kr => {
            if (this.isLinkedToGoogleSheet(kr)) {
              const { value, errors } = await this.getCurrentValue(kr)
              kr.isFetching = false
              if (errors) {
                this.errors[kr.id] = { current: errors }
              } else {
                kr.newCurrent = value
                if (value !== kr.current) {
                  this.markAsDirty(kr.id)
                }
              }
            }
          })
      })
    },
    onOwnershipChange(entityType, entity) {
      let goalIndex, krIndex
      switch (entityType) {
        case ENTITY_TYPE.GOAL:
          goalIndex = this.goals.findIndex(R.propEq('id', entity.id))
          this.goals = R.update(goalIndex, entity, this.goals)
          break
        case ENTITY_TYPE.KEY_RESULT:
          {
            goalIndex = this.goals.findIndex(R.propEq('id', entity.goalId))
            krIndex = this.goals[goalIndex].keyResults.findIndex(R.propEq('id', entity.id))
            const keyResults = R.update(krIndex, entity, this.goals[goalIndex].keyResults)
            const goal = R.assoc('keyResults', keyResults, this.goals[goalIndex])
            this.goals = R.update(goalIndex, goal, this.goals)
          }
          break
      }
    }
  },
  async mounted() {
    await this.fetchGoals()
    this.fetchSpreadSheetLinkedValues()
    EventBus.$on('SUBMIT_FORM', this.onSubmit)
    this.editId = editManager.enterEditMode()
    this.$nextTick(() => {
      if (this.goals.find(goal => goal.id === this.sourceGoalId)) {
        const goal = document.querySelector('#bulk-checkin-goal-' + this.sourceGoalId)
        goal && goal.scrollIntoView({ block: 'center' })
      }
    })
    trackEvent(EVENT_CATEGORY.NAVIGATION, EVENT_ACTION.BULK_CHECKIN_MODAL)
  },
  beforeDestroy() {
    EventBus.$off('SUBMIT_FORM', this.onSubmit)
    editManager.exitEditMode(this.editId)
  }
}
</script>

<style lang="scss" scoped>
@import '~@/styles/styleConfigurations';
@import '~@/styles/errors';
@import '~@/styles/components/input';
@import '~@/styles/components/kr';

$form-width: 925px;

.bulk-checkin {
  max-width: $form-width;
  margin: 10px 70px 0 70px;
}

.kr-circle-icon-wrapper {
  margin-right: 14px;
  @include dimension(24px, 24px);
}

.goal-icon {
  background-size: contain;
}

.goal-icon,
.kr-icon-greyed {
  height: 24px;
  width: 24px;
  margin-right: 14px;
}

.kr-icon-greyed {
  background-position: center;
}

.goal-meta {
  margin-bottom: 32px;
}

.kr-value-field {
  width: 110px;
  box-sizing: content-box;
}

input {
  padding: 0 10px;
  text-align: center;
  height: 30px;
}

/deep/ .checkin-status-select .widget-item:not(:last-child) {
  padding-right: 8px;
}

.kr {
  margin-bottom: 35px;
  position: relative;
}

.common-errors {
  margin-left: 38px;
  position: absolute;
}

.kr-value {
  &.percentage:after {
    content: '%';
  }

  &.money:before {
    content: '$';
  }
}

.status-select {
  padding-top: 5px;
}

.goal:not(:last-child) {
  margin-bottom: 55px;
}

.overdue-sign {
  position: absolute;
  left: -44px;
  top: 2px;
  display: flex;
  align-items: center;
}

.heading {
  margin-left: 64px;
}

.head-title {
  font-size: 2.25rem;
}

.goal-avatar {
  margin-right: 8px;
  display: block;
  margin-top: 1px;
}

.kr-avatar {
  margin-right: 8px;
  display: block;
  padding: 0 1px;
}

@include media-breakpoint-down(md) {
  .bulk-checkin {
    margin-left: 0;
    margin-right: 0;
  }
  .clock-icon {
    display: none;
  }
  .overdue-sign {
    left: -16px;
  }
  .heading {
    margin-left: 0;
  }
}
</style>
<style>
.bulk-checkin .custom-footer {
  max-width: 940px;
  margin: 0 auto;
  padding: 30px 0;
}
</style>
